<template>
	<V2Layout>
		<div class="v2b">

			<!-- ─── Page Header ─── -->
			<div class="v2b__head">
				<div class="v2b__page-icon">
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
				</div>
				<div>
					<div class="v2b__title">{{ $t('page_caption_billing') }}</div>
					<div class="v2b__sub">Balance, transactions and invoices</div>
				</div>
			</div>

			<!-- ─── Balance Hero (DSP only) ─── -->
			<div v-if="appSide === SIDE_DSP" class="v2b__hero">
				<div class="v2b__hero-left">
					<div class="v2b__hero-label">Total Balance</div>
					<div class="v2b__hero-amount">
						<span>{{ formattedTotalBalance }}</span>
						<span class="v2b__hero-ccy">{{ agency.currency }}</span>
					</div>

					<!-- Balance breakdown bar -->
					<div v-if="totalBalance > 0" class="v2b__hero-breakdown">
						<div class="v2b__hero-bar">
							<div class="v2b__hero-bar-prepaid" :style="{ width: prepaidPercent + '%' }" />
							<div class="v2b__hero-bar-credit" :style="{ width: creditPercent + '%' }" />
						</div>
						<div class="v2b__hero-legend">
							<div class="v2b__hero-legend-item">
								<span class="v2b__hero-dot-prepaid" />
								<div>
									<div class="v2b__hero-legend-label">Prepaid</div>
									<div class="v2b__hero-legend-value">{{ formattedPrepaidBalance }}</div>
								</div>
							</div>
							<div class="v2b__hero-legend-item">
								<span class="v2b__hero-dot-credit" />
								<div>
									<div class="v2b__hero-legend-label">Credit</div>
									<div class="v2b__hero-legend-value">{{ formattedCreditBalance }}</div>
								</div>
							</div>
						</div>
					</div>

					<div class="v2b__hero-meta">
						<span class="v2b__hero-meta-item">
							<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 17 13.5 8.5 8.5 13.5 2 7"/><polyline points="16 17 22 17 22 11"/></svg>
							Today's spend <b class="mono">{{ formattedTodaySpend }}</b>
						</span>
						<span v-if="lastTopupDate" class="v2b__hero-dot"></span>
						<span v-if="lastTopupDate" class="v2b__hero-meta-item">
							<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
							Last top-up <b>{{ lastTopupDate }}</b>
						</span>
					</div>
				</div>
				<div class="v2b__hero-actions">
					<router-link :to="{ name: 'v2-balance-add' }" class="v2b__cta-primary">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
						{{ $t('dashboard.add_balance') }}
					</router-link>
					<button class="v2b__cta-glass">
						<span class="v2b__cta-glass-left">
							<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
							Auto-recharge
						</span>
						<span class="v2b__pill-off">Off</span>
					</button>
				</div>
			</div>

			<!-- ─── KPI mini row (DSP only) ─── -->
			<div v-if="appSide === SIDE_DSP" class="v2b__kpi-row">
				<div class="v2b__kpi">
					<div class="v2b__kpi-icon v2b__kpi-icon--green">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>
					</div>
					<div class="v2b__kpi-body">
						<div class="v2b__kpi-label">Deposited (30d)</div>
						<div class="v2b__kpi-value">{{ stats.deposited }}</div>
						<div class="v2b__kpi-sub">{{ stats.depositedCount }} top-ups</div>
					</div>
				</div>
				<div class="v2b__kpi">
					<div class="v2b__kpi-icon v2b__kpi-icon--red">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/></svg>
					</div>
					<div class="v2b__kpi-body">
						<div class="v2b__kpi-label">Spent (30d)</div>
						<div class="v2b__kpi-value">{{ stats.spent }}</div>
						<div class="v2b__kpi-sub">across {{ stats.spentCount }} campaigns</div>
					</div>
				</div>
				<div class="v2b__kpi">
					<div class="v2b__kpi-icon v2b__kpi-icon--yellow">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
					</div>
					<div class="v2b__kpi-body">
						<div class="v2b__kpi-label">Pending invoices</div>
						<div class="v2b__kpi-value">{{ pendingInvoicesCount }} · {{ pendingInvoicesAmount }}</div>
						<div class="v2b__kpi-sub">awaiting payment</div>
					</div>
				</div>
			</div>

			<!-- ─── Tabs ─── -->
			<div class="v2b__tabs">
				<button
					class="v2b__tab"
					:class="{ 'v2b__tab--active': activeTab === 'transactions' }"
					@click="activeTab = 'transactions'"
				>
					Transactions <span class="v2b__tab-count">{{ transactionsListTotalCount }}</span>
				</button>
				<button
					v-if="appSide === SIDE_DSP"
					class="v2b__tab"
					:class="{ 'v2b__tab--active': activeTab === 'invoices' }"
					@click="activeTab = 'invoices'"
				>
					Invoices <span class="v2b__tab-count">{{ invoicesListTotalCount }}</span>
				</button>
			</div>

			<!-- ─── Transactions Tab ─── -->
			<div v-show="activeTab === 'transactions'" class="v2b__card">
				<div class="v2b__card-head">
					<div>
						<div class="v2b__card-title">Transactions</div>
						<div class="v2b__card-sub">All balance movements across your account</div>
					</div>
					<div class="v2b__filter-bar">
						<div class="v2b__search">
							<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
							<input v-model="transactionsSearch" type="text" placeholder="Search…" />
						</div>
						<div class="v2b__pill-group">
							<button
								v-for="opt in transactionFilters"
								:key="opt.value"
								class="v2b__filter-pill"
								:class="{ 'v2b__filter-pill--active': transactionsFilter === opt.value }"
								@click="transactionsFilter = opt.value"
							>{{ opt.label }}</button>
						</div>
					</div>
				</div>

				<table class="v2b__table">
					<thead>
						<tr>
							<th style="width: 90px;">{{ $t('transactions.id') | capitalize }}</th>
							<th style="width: 130px;">{{ $t('transactions.amount') | capitalize }}</th>
							<th>{{ $t('transactions.correspondent') | capitalize }}</th>
							<th style="width: 100px;">{{ $t('transactions.campaign_id') | capitalize }}</th>
							<th>{{ $t('transactions.description') | capitalize }}</th>
							<th style="width: 130px;">{{ $t('transactions.time') | capitalize }}</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="tx in transactionsList" :key="tx.id">
							<td class="v2b__td-id">#{{ tx.id }}</td>
							<td v-if="tx.credit && tx.credit.account == agency.id" class="v2b__td-amount v2b__td-amount--out">
								&minus;{{ tx.amount | formatNumber }}
							</td>
							<td v-else-if="tx.debit && tx.debit.account == agency.id" class="v2b__td-amount v2b__td-amount--in">
								+{{ tx.amount | formatNumber }}
							</td>
							<td v-else class="v2b__td-amount">{{ tx.amount | formatNumber }}</td>

							<td>
								<div class="v2b__corr">
									<div
										class="v2b__corr-icon"
										:class="getCorrespondentDirection(tx)"
									>
										<svg v-if="getCorrespondentDirection(tx) === 'v2b__corr-icon--in'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>
										<svg v-else-if="getCorrespondentDirection(tx) === 'v2b__corr-icon--out'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/></svg>
										<svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
									</div>
									<div class="v2b__corr-text">
										<div class="v2b__corr-name">{{ getCorrespondentName(tx) }}</div>
									</div>
								</div>
							</td>

							<td>
								<span v-if="tx.campaign_id" class="v2b__campaign-pill">#{{ tx.campaign_id }}</span>
								<span v-else class="v2b__dash">&mdash;</span>
							</td>

							<td class="v2b__td-desc">{{ tx.description || '—' }}</td>
							<td class="v2b__td-time">{{ formatDateTime(tx.ctime) }}</td>
						</tr>
						<tr v-if="!transactionsList.length">
							<td colspan="6">
								<div class="v2b__empty">
									<div class="v2b__empty-icon">
										<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
									</div>
									<div class="v2b__empty-title">No transactions yet</div>
									<div class="v2b__empty-sub">Top up your balance to get started. Spends and refunds will appear here.</div>
									<router-link
										v-if="appSide === SIDE_DSP"
										:to="{ name: 'v2-balance-add' }"
										class="v2b__btn v2b__btn--primary"
										style="margin-top: 12px;"
									>
										<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
										{{ $t('dashboard.add_balance') }}
									</router-link>
								</div>
							</td>
						</tr>
					</tbody>
				</table>

				<div v-if="transactionsList.length" class="v2b__table-footer">
					<div class="v2b__footer-meta">
						<span>Total <b class="mono">{{ transactionsListTotalCount }}</b> transactions</span>
					</div>
					<TablePaginator
						v-model="transactionsPagination"
						:total="transactionsListTotalCount"
						:paginator-props="{ queryKey: 'p' }"
						class="v2b__paginator"
						@input="handleTransactionsPaginationChange"
					/>
				</div>
			</div>

			<!-- ─── Invoices Tab (DSP only) ─── -->
			<div
				v-if="appSide === SIDE_DSP"
				v-show="activeTab === 'invoices'"
				class="v2b__card"
			>
				<div class="v2b__card-head">
					<div>
						<div class="v2b__card-title">Invoices</div>
						<div class="v2b__card-sub">Bank transfer invoices issued for top-ups</div>
					</div>
					<div class="v2b__filter-bar">
						<div class="v2b__pill-group">
							<button
								v-for="opt in invoiceFilters"
								:key="opt.value"
								class="v2b__filter-pill"
								:class="{ 'v2b__filter-pill--active': invoicesFilter === opt.value }"
								@click="invoicesFilter = opt.value"
							>{{ opt.label }}</button>
						</div>
					</div>
				</div>

				<table class="v2b__table">
					<thead>
						<tr>
							<th style="width: 180px;">{{ $t('documents.invoice_id') | capitalize }}</th>
							<th style="width: 130px;">{{ $t('documents.invoice_date') | capitalize }}</th>
							<th style="width: 180px;">{{ $t('documents.invoice_total_excl_tax') | capitalize }}</th>
							<th>{{ $t('documents.invoice_status') | capitalize }}</th>
							<th style="width: 160px; text-align: right;">&nbsp;</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="invoice in filteredInvoices" :key="invoice.id">
							<td><span class="v2b__inv-num">{{ invoice.number }}</span></td>
							<td class="v2b__td-time">{{ formatDate(invoice.date) }}</td>
							<td class="v2b__inv-amount" v-html="formatCash(invoice.total_excl_tax, agencyCurrency)" />
							<td>
								<span class="v2b__status-pill" :class="getInvoiceStatusClass(invoice.status)">
									<span class="v2b__status-dot" />
									{{ $t(`documents.invoice_status_${invoice.status}`) | capitalize }}
								</span>
							</td>
							<td style="text-align: right;">
								<a
									v-if="invoice.status == INVOICE_STATUS_PENDING"
									:href="invoice.payment_gateway_url"
									target="_blank"
									class="v2b__btn v2b__btn--primary v2b__btn--sm"
								>
									{{ $t('billing.pay') }} &rarr;
								</a>
								<a
									v-else-if="invoice.invoice_document"
									:href="invoice.invoice_document.file"
									target="_blank"
									class="v2b__btn v2b__btn--outline v2b__btn--sm"
								>
									<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
									{{ $t('documents.invoice') }}
								</a>
								<span v-else class="v2b__dash">&mdash;</span>
							</td>
						</tr>
						<tr v-if="!filteredInvoices.length">
							<td colspan="5">
								<div class="v2b__empty">
									<div class="v2b__empty-icon">
										<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
									</div>
									<div class="v2b__empty-title">No invoices found</div>
									<div class="v2b__empty-sub">Invoices appear here after you initiate a bank transfer top-up.</div>
								</div>
							</td>
						</tr>
					</tbody>
				</table>

				<div v-if="filteredInvoices.length" class="v2b__table-footer">
					<div class="v2b__footer-meta">
						<span>Total <b class="mono">{{ invoicesListTotalCount }}</b> invoices</span>
					</div>
					<TablePaginator
						v-model="invoicesPagination"
						:total="invoicesListTotalCount"
						:paginator-props="{ queryKey: 'p' }"
						class="v2b__paginator"
						@input="handleInvoicesPaginationChange"
					/>
				</div>
			</div>

			<!-- Balance Add modal rendered by child route -->
			<router-view />
		</div>
	</V2Layout>
</template>


<script>
import V2Layout from '@/layouts/V2Layout';
import OriginalBilling from '@/ui-overrides/default/pages/billing/index.vue';
import { INVOICE_STATUS_PENDING } from '@/constants/billing';
import { profileFormatDate as formatDateOnly } from '@/utilites';

export default {
	name: 'V2Billing',

	extends: OriginalBilling,

	components: {
		...OriginalBilling.components,
		V2Layout,
	},

	data() {
		return {
			...OriginalBilling.data.call(this),
			activeTab: 'transactions',
			transactionsSearch: '',
			transactionsFilter: 'all',
			invoicesFilter: 'all',
		};
	},

	computed: {
		prepaidBalance() {
			return Number((this.agency && this.agency.balance) || 0);
		},
		creditBalance() {
			return Number((this.agency && this.agency.credit_balance) || 0);
		},
		totalBalance() {
			return this.prepaidBalance + this.creditBalance;
		},
		prepaidPercent() {
			if (!this.totalBalance) return 50;
			return Math.round((this.prepaidBalance / this.totalBalance) * 100);
		},
		creditPercent() {
			if (!this.totalBalance) return 50;
			return 100 - this.prepaidPercent;
		},
		formattedPrepaidBalance() {
			return this.formatMoney(this.prepaidBalance);
		},
		formattedCreditBalance() {
			return this.formatMoney(this.creditBalance);
		},
		formattedTotalBalance() {
			return this.formatMoney(this.totalBalance);
		},
		formattedBalance() {
			// Legacy alias — kept for backward compatibility
			return this.formattedTotalBalance;
		},
		formattedTodaySpend() {
			// Placeholder: real value would come from a 'today spend' API.
			// Fall back to 0 in agency currency.
			return this.formatMoney(0);
		},
		lastTopupDate() {
			const inTx = (this.transactionsList || []).find(t => t.debit && t.debit.account == this.agency.id);
			if (!inTx) return null;
			try {
				return formatDateOnly(inTx.ctime);
			} catch (e) {
				return null;
			}
		},
		stats() {
			const list = this.transactionsList || [];
			let dep = 0, depCount = 0, spent = 0, campaignIds = new Set();
			list.forEach(t => {
				if (t.debit && t.debit.account == this.agency.id) {
					dep += Number(t.amount) || 0;
					depCount++;
				} else if (t.credit && t.credit.account == this.agency.id) {
					spent += Number(t.amount) || 0;
					if (t.campaign_id) campaignIds.add(t.campaign_id);
				}
			});
			return {
				deposited: this.formatMoney(dep, '+'),
				depositedCount: depCount,
				spent: this.formatMoney(spent, '−'),
				spentCount: campaignIds.size,
			};
		},
		pendingInvoicesCount() {
			return (this.invoicesList || []).filter(i => i.status == this.INVOICE_STATUS_PENDING).length;
		},
		pendingInvoicesAmount() {
			const sum = (this.invoicesList || [])
				.filter(i => i.status == this.INVOICE_STATUS_PENDING)
				.reduce((acc, i) => acc + Number(i.total_excl_tax || 0), 0);
			return this.formatMoney(sum);
		},
		transactionFilters() {
			return [
				{ value: 'all', label: 'All' },
				{ value: 'topups', label: 'Top-ups' },
				{ value: 'spend', label: 'Spend' },
				{ value: 'refunds', label: 'Refunds' },
			];
		},
		invoiceFilters() {
			return [
				{ value: 'all', label: 'All' },
				{ value: 'pending', label: 'Pending' },
				{ value: 'paid', label: 'Paid' },
				{ value: 'cancelled', label: 'Cancelled' },
			];
		},
		filteredInvoices() {
			if (this.invoicesFilter === 'all') return this.invoicesList;
			return (this.invoicesList || []).filter(i => String(i.status) === this.invoicesFilter);
		},
	},

	methods: {
		formatMoney(value, sign) {
			const num = Number(value) || 0;
			const ccy = (this.agency && this.agency.currency) || '';
			const fmt = num.toLocaleString(undefined, {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			});
			const prefix = sign || '';
			return `${prefix}${fmt} ${ccy}`.trim();
		},
		getCorrespondentDirection(tx) {
			if (tx.credit && tx.credit.account == this.agency.id) return 'v2b__corr-icon--out';
			if (tx.debit && tx.debit.account == this.agency.id) return 'v2b__corr-icon--in';
			return 'v2b__corr-icon--sys';
		},
		getCorrespondentName(tx) {
			if (tx.credit && tx.credit.account == this.agency.id) {
				return tx.debit && tx.debit.name ? tx.debit.name : this.$options.filters.capitalize(this.$t('transactions.cash'));
			}
			if (tx.debit && tx.debit.account == this.agency.id) {
				return tx.credit && tx.credit.name ? tx.credit.name : this.$options.filters.capitalize(this.$t('transactions.adding_funds'));
			}
			return '—';
		},
		getInvoiceStatusClass(status) {
			if (status === 'paid' || status == 2) return 'v2b__status-pill--paid';
			if (status === 'cancelled' || status === 'canceled' || status == 3) return 'v2b__status-pill--cancel';
			return 'v2b__status-pill--pending';
		},
	},
};
</script>


<style lang="sass" scoped>
@import '~@/assets/styles/v2-page-overrides'
@import '~@/assets/styles/v2-tokens.sass'

$primary: $v2-teal
$primary-dark: darken($v2-teal, 6%)
$accent: $v2-purple
$text: $v2-text
$muted: $v2-text-muted
$border: $v2-border
$card: $v2-card-bg
$bg: $v2-bg
$green: $v2-green
$orange: $v2-orange
$red: $v2-red
$blue: $v2-blue
$font: $v2-font
$mono: 'JetBrains Mono', 'Menlo', monospace

.mono
	font-family: $mono
	font-feature-settings: 'tnum'

.v2b
	max-width: 1240px
	margin: 0 auto
	padding: 24px
	display: flex
	flex-direction: column
	gap: 18px
	font-family: $font
	font-size: 14px
	color: $text

// ─── Page head ───
.v2b__head
	display: flex
	align-items: center
	gap: 14px

.v2b__page-icon
	width: 44px
	height: 44px
	border-radius: 12px
	background: rgba($primary, 0.1)
	color: $primary
	display: flex
	align-items: center
	justify-content: center

	svg
		width: 22px
		height: 22px

.v2b__title
	font-size: 22px
	font-weight: 700
	letter-spacing: -0.02em

.v2b__sub
	font-size: 12.5px
	color: $muted
	margin-top: 2px

// ─── Balance hero ───
.v2b__hero
	background: radial-gradient(circle at 85% -10%, rgba(255,255,255,0.18), transparent 55%), radial-gradient(circle at 0% 110%, rgba(255,255,255,0.08), transparent 50%), linear-gradient(120deg, $primary 0%, $primary-dark 55%, $accent 100%)
	color: #fff
	border-radius: 20px
	padding: 28px 32px
	display: grid
	grid-template-columns: 1.5fr 1fr
	gap: 24px
	box-shadow: 0 18px 40px -18px rgba($primary, 0.55)
	position: relative
	overflow: hidden

	@media (max-width: 768px)
		grid-template-columns: 1fr
		padding: 22px

.v2b__hero-label
	font-size: 10px
	font-weight: 700
	text-transform: uppercase
	letter-spacing: 0.16em
	color: rgba(255,255,255,0.7)
	margin-bottom: 10px

.v2b__hero-amount
	font-family: $mono
	font-weight: 700
	font-size: 44px
	letter-spacing: -0.02em
	display: flex
	align-items: baseline
	gap: 10px

	@media (max-width: 768px)
		font-size: 36px

.v2b__hero-ccy
	font-size: 18px
	opacity: 0.7
	font-weight: 500

.v2b__hero-left
	min-width: 0

// Balance breakdown bar (prepaid/credit)
.v2b__hero-breakdown
	margin-top: 20px
	max-width: 460px

.v2b__hero-bar
	display: flex
	height: 8px
	border-radius: 999px
	overflow: hidden
	background: rgba(255,255,255,0.15)

.v2b__hero-bar-prepaid
	height: 100%
	background: linear-gradient(90deg, #ffffff, rgba(255,255,255,0.85))
	transition: width 0.3s

.v2b__hero-bar-credit
	height: 100%
	background: linear-gradient(90deg, rgba(255,255,255,0.45), rgba(255,255,255,0.30))
	transition: width 0.3s

.v2b__hero-legend
	margin-top: 12px
	display: flex
	gap: 24px
	flex-wrap: wrap

.v2b__hero-legend-item
	display: inline-flex
	align-items: center
	gap: 8px

.v2b__hero-dot-prepaid,
.v2b__hero-dot-credit
	width: 10px
	height: 10px
	border-radius: 50%
	flex-shrink: 0

.v2b__hero-dot-prepaid
	background: #ffffff

.v2b__hero-dot-credit
	background: rgba(255,255,255,0.45)
	border: 1px solid rgba(255,255,255,0.6)

.v2b__hero-legend-label
	font-size: 10px
	font-weight: 700
	text-transform: uppercase
	letter-spacing: 0.12em
	color: rgba(255,255,255,0.65)

.v2b__hero-legend-value
	font-family: $mono
	font-weight: 700
	font-size: 16px
	color: #ffffff
	margin-top: 2px

.v2b__hero-meta
	margin-top: 16px
	display: flex
	align-items: center
	gap: 14px
	flex-wrap: wrap
	font-size: 12.5px
	color: rgba(255,255,255,0.78)

.v2b__hero-meta-item
	display: inline-flex
	align-items: center
	gap: 6px

	svg
		width: 14px
		height: 14px

	b
		color: #fff
		font-weight: 600

.v2b__hero-dot
	width: 3px
	height: 3px
	border-radius: 50%
	background: rgba(255,255,255,0.4)

.v2b__hero-actions
	display: flex
	flex-direction: column
	gap: 10px
	justify-content: center

.v2b__cta-primary
	display: inline-flex
	align-items: center
	justify-content: center
	gap: 8px
	height: 48px
	padding: 0 22px
	border-radius: 12px
	background: #fff
	color: $primary
	font-weight: 700
	font-size: 14px
	font-family: $font
	box-shadow: 0 6px 18px -6px rgba(0,0,0,0.2)
	transition: transform 0.15s
	text-decoration: none

	&:hover
		transform: translateY(-1px)
		text-decoration: none

	svg
		width: 18px
		height: 18px

.v2b__cta-glass
	display: inline-flex
	align-items: center
	justify-content: space-between
	gap: 8px
	height: 44px
	padding: 0 18px
	border-radius: 12px
	background: rgba(255,255,255,0.1)
	color: #fff
	font-weight: 600
	font-size: 13.5px
	font-family: $font
	border: 1px solid rgba(255,255,255,0.25)
	backdrop-filter: blur(6px)
	cursor: pointer

	&:hover
		background: rgba(255,255,255,0.15)

.v2b__cta-glass-left
	display: inline-flex
	align-items: center
	gap: 8px

	svg
		width: 16px
		height: 16px

.v2b__pill-off
	font-size: 10px
	font-weight: 700
	text-transform: uppercase
	letter-spacing: 0.1em
	padding: 3px 8px
	border-radius: 999px
	background: rgba(255,255,255,0.18)

// ─── KPI ───
.v2b__kpi-row
	display: grid
	grid-template-columns: repeat(3, 1fr)
	gap: 14px

	@media (max-width: 768px)
		grid-template-columns: 1fr

.v2b__kpi
	background: $card
	border: 1px solid $border
	border-radius: 16px
	padding: 18px 20px
	display: flex
	gap: 14px
	align-items: center
	box-shadow: 0 1px 2px rgba($text, 0.03)

.v2b__kpi-icon
	width: 40px
	height: 40px
	border-radius: 12px
	display: flex
	align-items: center
	justify-content: center
	flex-shrink: 0

	svg
		width: 20px
		height: 20px

	&--green
		background: rgba($green, 0.12)
		color: $green
	&--red
		background: rgba($red, 0.1)
		color: $red
	&--yellow
		background: rgba($orange, 0.15)
		color: darken($orange, 8%)

.v2b__kpi-body
	display: flex
	flex-direction: column
	gap: 2px
	min-width: 0

.v2b__kpi-label
	font-size: 11.5px
	color: $muted
	font-weight: 500
	text-transform: uppercase
	letter-spacing: 0.06em

.v2b__kpi-value
	font-family: $mono
	font-weight: 700
	font-size: 20px
	letter-spacing: -0.01em

.v2b__kpi-sub
	font-size: 11.5px
	color: $muted
	margin-top: 2px

// ─── Tabs ───
.v2b__tabs
	display: inline-flex
	gap: 4px
	background: $card
	padding: 4px
	border-radius: 12px
	border: 1px solid $border
	width: fit-content

.v2b__tab
	display: inline-flex
	align-items: center
	gap: 8px
	height: 36px
	padding: 0 16px
	border-radius: 8px
	font-size: 13px
	font-weight: 600
	color: $muted
	font-family: $font
	cursor: pointer

	&--active
		background: rgba($primary, 0.1)
		color: $primary

.v2b__tab-count
	font-family: $mono
	font-size: 11px
	font-weight: 700
	padding: 2px 7px
	border-radius: 999px
	background: rgba($bg, 0.8)
	color: $muted

	.v2b__tab--active &
		background: $primary
		color: #fff

// ─── Card ───
.v2b__card
	background: $card
	border: 1px solid $border
	border-radius: 16px
	box-shadow: 0 1px 2px rgba($text, 0.03)
	overflow: hidden

.v2b__card-head
	padding: 18px 22px
	display: flex
	align-items: center
	justify-content: space-between
	gap: 16px
	border-bottom: 1px solid $border
	flex-wrap: wrap

.v2b__card-title
	font-size: 15px
	font-weight: 700

.v2b__card-sub
	font-size: 12px
	color: $muted
	margin-top: 2px

// ─── Filter bar ───
.v2b__filter-bar
	display: flex
	align-items: center
	gap: 8px
	flex-wrap: wrap

.v2b__search
	position: relative
	display: inline-flex
	align-items: center

	svg
		position: absolute
		left: 10px
		width: 14px
		height: 14px
		color: $muted
		pointer-events: none

	input
		height: 36px
		padding: 0 12px 0 32px
		border-radius: 10px
		border: 1px solid $border
		background: $card
		font-size: 13px
		width: 220px
		outline: none
		font-family: $font
		color: $text

		&:focus
			border-color: $primary
			box-shadow: 0 0 0 3px rgba($primary, 0.12)

.v2b__pill-group
	display: inline-flex
	padding: 3px
	border-radius: 10px
	background: rgba($bg, 0.7)
	gap: 2px

.v2b__filter-pill
	height: 30px
	padding: 0 12px
	border-radius: 8px
	font-size: 12.5px
	font-weight: 600
	color: $muted
	display: inline-flex
	align-items: center
	background: transparent
	border: none
	font-family: $font
	cursor: pointer

	&--active
		background: $card
		color: $text
		box-shadow: 0 1px 2px rgba($text, 0.06)

// ─── Table ───
.v2b__table
	width: 100%
	border-collapse: collapse

	thead th
		text-align: left
		font-size: 11px
		font-weight: 600
		text-transform: uppercase
		letter-spacing: 0.06em
		color: $muted
		padding: 12px 18px
		background: rgba($bg, 0.5)
		border-bottom: 1px solid $border

	tbody td
		padding: 14px 18px
		font-size: 13px
		vertical-align: middle
		border-bottom: 1px solid rgba($border, 0.7)

	tbody tr:hover td
		background: rgba($bg, 0.35)

	tbody tr:last-child td
		border-bottom: 0

.v2b__td-id
	font-family: $mono
	color: $muted
	font-size: 12px

.v2b__td-amount
	font-family: $mono
	font-weight: 700
	font-size: 14px
	white-space: nowrap

	&--in
		color: $green
	&--out
		color: $red

.v2b__td-time
	font-family: $mono
	font-size: 12px
	color: $muted
	white-space: nowrap

.v2b__td-desc
	color: $muted
	max-width: 240px
	overflow: hidden
	text-overflow: ellipsis
	white-space: nowrap

// Correspondent
.v2b__corr
	display: inline-flex
	align-items: center
	gap: 10px

.v2b__corr-icon
	width: 28px
	height: 28px
	border-radius: 8px
	display: inline-flex
	align-items: center
	justify-content: center
	flex-shrink: 0

	svg
		width: 14px
		height: 14px

	&--in
		background: rgba($green, 0.12)
		color: $green
	&--out
		background: rgba($red, 0.1)
		color: $red
	&--sys
		background: rgba($bg, 0.8)
		color: $muted

.v2b__corr-text
	display: flex
	flex-direction: column
	gap: 1px

.v2b__corr-name
	font-size: 13px
	font-weight: 600

.v2b__campaign-pill
	display: inline-block
	font-family: $mono
	font-size: 11.5px
	font-weight: 600
	padding: 3px 8px
	border-radius: 6px
	background: rgba($accent, 0.12)
	color: $accent

.v2b__dash
	color: $muted

// Invoice
.v2b__inv-num
	font-family: $mono
	font-weight: 700
	font-size: 13px

.v2b__inv-amount
	font-family: $mono
	font-weight: 700
	font-size: 13px

// Status pill
.v2b__status-pill
	display: inline-flex
	align-items: center
	gap: 6px
	font-size: 10.5px
	font-weight: 700
	text-transform: uppercase
	letter-spacing: 0.08em
	padding: 4px 10px
	border-radius: 999px

	&--pending
		background: rgba($orange, 0.15)
		color: darken($orange, 10%)

		.v2b__status-dot
			background: $orange

	&--paid
		background: rgba($green, 0.12)
		color: $green

		.v2b__status-dot
			background: $green

	&--cancel
		background: rgba($red, 0.1)
		color: $red

		.v2b__status-dot
			background: $red

.v2b__status-dot
	width: 6px
	height: 6px
	border-radius: 50%

// ─── Buttons ───
.v2b__btn
	display: inline-flex
	align-items: center
	justify-content: center
	gap: 6px
	height: 32px
	padding: 0 14px
	border-radius: 8px
	font-size: 12.5px
	font-weight: 600
	font-family: $font
	border: 1px solid transparent
	cursor: pointer
	text-decoration: none

	&:hover
		text-decoration: none

	svg
		width: 14px
		height: 14px

	&--sm
		height: 32px

	&--primary
		background: $primary
		color: #fff

		&:hover
			background: $primary-dark
			color: #fff

	&--outline
		background: $card
		border-color: $border
		color: $text

		&:hover
			background: rgba($bg, 0.5)

// ─── Table footer ───
.v2b__table-footer
	display: flex
	align-items: center
	justify-content: space-between
	padding: 14px 22px
	gap: 16px
	flex-wrap: wrap
	border-top: 1px solid $border
	background: rgba($bg, 0.3)

.v2b__footer-meta
	font-size: 12.5px
	color: $muted

	b
		color: $text
		font-family: $mono
		font-weight: 600

.v2b__paginator
	::v-deep
		.table-paginator,
		.pagination
			gap: 4px

// ─── Empty ───
.v2b__empty
	padding: 60px 20px
	display: flex
	flex-direction: column
	align-items: center
	gap: 8px
	text-align: center

.v2b__empty-icon
	width: 64px
	height: 64px
	border-radius: 18px
	background: rgba($bg, 0.8)
	color: $muted
	display: flex
	align-items: center
	justify-content: center
	margin-bottom: 4px

	svg
		width: 28px
		height: 28px

.v2b__empty-title
	font-size: 15px
	font-weight: 600
	color: $text

.v2b__empty-sub
	font-size: 13px
	color: $muted
	max-width: 360px
</style>
